import { cacheData, loadData } from '~app/actions/cache';
import { loadSpeciesInfo } from '~app/actions/database';

import defined from '~app/core/defined';
import preloadImage from '~app/core/preloadImage';

export const SET_SPECIES_LIST = 'SET_SPECIES_LIST';
export const SET_CLASS_FILTER = 'SET_CLASS_FILTER';
export const SET_THREAT_FILTER = 'SET_THREAT_FILTER';
export const SET_FILTERED_SPECIES = 'SET_FILTERED_SPECIES';
export const SET_THREAT_LEVEL_COUNTS = 'SET_THREAT_LEVEL_COUNTS';
export const SELECT_SPECIES = 'SELECT_SPECIES';
export const LOADED_SPECIES_CONTENT = 'LOADED_SPECIES_CONTENT';
export const LOADED_SPECIES_IMAGE = 'LOADED_SPECIES_IMAGE';
export const CLEAR_SPECIES_IMAGE = 'CLEAR_SPECIES_IMAGE';
export const CLEAR_SELECTED_SPECIES = 'CLEAR_SELECTED_SPECIES';

const WILDLIFE_IMAGE_URL = `${process.env.WILDLIFE_IMAGE_URL}`;

export const WildlifeClass = Object.freeze({
  amphibian: 1,
  mammal: 2,
  reptile: 3,
});

export const ThreatLevel = Object.freeze({
  none:                 (0),
  leastConcern:         (1 << 0),
  nearThreatened:       (1 << 1),
  vulnerable:           (1 << 2),
  endangered:           (1 << 3),
  criticallyEndangered: (1 << 4),
  all:                  ((1 << 5) - 1),
});

export function resetWildlife(context, speciesList, wildlifeClass, threatFilter) {
  return (dispatch) => {
    dispatch(setSpeciesList(context, speciesList));
    dispatch(setClassFilter(context, wildlifeClass, false));
    dispatch(setThreatFilter(context, threatFilter, false));
    dispatch(applyFilter(context));
  };
}

function setSpeciesList(context, speciesList) {
  return (dispatch) => {
    // Make sure only supported species are included in the species list.
    const filteredSpecies = speciesList.filter((species) => {
      return (
        defined(convertDatabaseThreatLevel(species.threat_level)) &&
        defined(convertDatabaseRegionPresence(species.region_presence)) &&
        defined(species.threat_citation) &&
        defined(species.region_citation)
      );
    });

    let countAmphibians = 0;
    let countMammals = 0;
    let countReptiles = 0;
    for (const species of filteredSpecies) {
      switch (convertDatabaseWildlifeClass(species.class_)) {
        case WildlifeClass.amphibian:
          ++countAmphibians;
          break;
        case WildlifeClass.mammal:
          ++countMammals;
          break;
        case WildlifeClass.reptile:
          ++countReptiles;
          break;
        default:
          throw new Error(`Unexpected wildlife class: ${species.class_}`);
      }
    }

    dispatch({
      type: SET_SPECIES_LIST,
      context,
      speciesList: filteredSpecies,
      countAmphibians,
      countMammals,
      countReptiles,
    });
  };
}

export function setClassFilter(context, classFilter, applyImmediately = true) {
  return (dispatch) => {
    dispatch({
      type: SET_CLASS_FILTER,
      context,
      classFilter,
    });

    if (applyImmediately) {
      dispatch(applyFilter(context));
    }
  };
}

export function setThreatFilter(context, threatFilter, applyImmediately = true) {
  return (dispatch) => {
    dispatch({
      type: SET_THREAT_FILTER,
      context,
      threatFilter,
    });

    if (applyImmediately) {
      dispatch(applyFilter(context));
    }
  };
}

export function applyFilter(context) {
  return (dispatch, getState) => {
    const speciesList = getState().wildlife[context].speciesList;
    const classFilter = getState().wildlife[context].activeClassFilter;
    const threatFilter = getState().wildlife[context].activeThreatFilter;

    const filteredSpecies = speciesList.filter((species) => {
      return (
        classFilter === convertDatabaseWildlifeClass(species.class_) &&
        (threatFilter & convertDatabaseThreatLevel(species.threat_level))
      );
    });

    dispatch({
      type: SET_FILTERED_SPECIES,
      context,
      filteredSpecies,
    });

    dispatch(updateThreatLevelCounts(context));
  };
}

function updateThreatLevelCounts(context) {
  return (dispatch, getState) => {
    const speciesList = getState().wildlife[context].speciesList;
    const classFilter = getState().wildlife[context].activeClassFilter;
    const classFilteredSpecies = speciesList.filter((species) => {
      return (
        classFilter === convertDatabaseWildlifeClass(species.class_)
      );
    });

    // Start with a fresh set of empty threat levels and tally up the species
    // falling into each one.
    const threatLevelCounts =
      classFilteredSpecies.reduce(
        (threatLevelCounts, species) => {
          addSpeciesToThreatLevel(species, threatLevelCounts);
          return threatLevelCounts;
        },
        createEmptyThreatLevels()
      );

    dispatch({
      type: SET_THREAT_LEVEL_COUNTS,
      context,
      threatLevelCounts,
    });
  };
}

function addSpeciesToThreatLevel(species, threatLevelCounts) {
  for (const threatLevelCount of threatLevelCounts) {
    if (threatLevelCount.levelCode === convertDatabaseThreatLevel(species.threat_level)) {
      ++threatLevelCount.speciesCount;
      return;
    }
  }

  throw new Error(`Unexpected threat level (${species.threat_level} on species: ${species.binomial})`);
}

export function threatLevelName(databaseThreatLevel) {
  return _threatLevelName(convertDatabaseThreatLevel(databaseThreatLevel));
}

function _threatLevelName(threatLevel) {
  let threatName = undefined;

  switch (threatLevel) {
    case ThreatLevel.leastConcern:
      threatName = "Least Concern";
      break;
    case ThreatLevel.nearThreatened:
     threatName = "Near Threatened";
     break;
    case ThreatLevel.vulnerable:
      threatName = "Vulnerable";
      break;
    case ThreatLevel.endangered:
      threatName = "Endangered";
      break;
    case ThreatLevel.criticallyEndangered:
      threatName = "Critically Endangered";
      break;
    default:
      throw new Error(`Unexpected threat level code: ${threatLevel}`);
  }

  return threatName;
}

function createEmptyThreatLevels() {
  return [
    {
      levelName: _threatLevelName(ThreatLevel.leastConcern),
      levelCode: ThreatLevel.leastConcern,
      speciesCount: 0,
    },
    {
      levelName: _threatLevelName(ThreatLevel.nearThreatened),
      levelCode: ThreatLevel.nearThreatened,
      speciesCount: 0,
    },
    {
      levelName: _threatLevelName(ThreatLevel.vulnerable),
      levelCode: ThreatLevel.vulnerable,
      speciesCount: 0,
    },
    {
      levelName: _threatLevelName(ThreatLevel.endangered),
      levelCode: ThreatLevel.endangered,
      speciesCount: 0,
    },
    {
      levelName: _threatLevelName(ThreatLevel.criticallyEndangered),
      levelCode: ThreatLevel.criticallyEndangered,
      speciesCount: 0,
    },
  ];
}

export function selectSpecies(context, species) {
  return async (dispatch, getState) => {
    if (defined(species)) {
      // Overwrite the database presence value with a converted value.
      dispatch({
        type: SELECT_SPECIES,
        context,
        summary: {
          ...species,
          region_presence: convertDatabaseRegionPresence(species.region_presence),
        },
      });

      const content = await dispatch(loadSpeciesInfo(species.binomial));

      // Make sure the image sheet with our thumbnail is always preloaded and
      // ready to go.
      const imageSheet = getState().database.imageSheets[content.image_sheet];
      await preloadImage(imageSheet);

      dispatch({
        type: LOADED_SPECIES_CONTENT,
        context,
        content,
      });

      await dispatch(loadSpeciesImage(context, content));
    }
    else {
      dispatch({
        type: CLEAR_SELECTED_SPECIES,
        context,
      });
    }
  };
}

function computeImageName(speciesContent) {
  const sheet = speciesContent.image_sheet;
  const x = speciesContent.image_x;
  const y = speciesContent.image_y;

  if (defined(sheet) && defined(x) && defined(y)) {
    return `wildlife-${sheet}-${x}-${y}.jpg`;
  }
  return null;
}

function loadSpeciesImage(context, speciesContent) {
  return async (dispatch) => {
    const imageName = computeImageName(speciesContent);
    if (defined(imageName)) {

      let imageObjectUrl = dispatch(loadData('wildlife-images', imageName));
      if (!defined(imageObjectUrl)) {
        // Clear the old high-res image after we have a new thumbnail to replace it.
        // Otherwise, our old high-res image reverts to the old thumbnail during
        // the loading for the next species, which is undesireable.
        dispatch(clearSpeciesImage(context));

        const response = await fetch(`${WILDLIFE_IMAGE_URL}/${imageName}`);
        const imageData = await response.blob();
        imageObjectUrl = defined(imageData) ? URL.createObjectURL(imageData) : null;
        dispatch(cacheData('wildlife-images', imageName, imageObjectUrl, (staleData) => {
          URL.revokeObjectURL(staleData);
        }));
      }

      // Always preload the image, even if it was in the cache.
      // We need to make sure it is always immediately ready for display.
      await preloadImage(imageObjectUrl);

      dispatch({
        type: LOADED_SPECIES_IMAGE,
        context,
        imageObjectUrl,
        binomial: speciesContent.binomial,
      });
    }
    else {
      // If there is no new image, then we must clear the old image.
      dispatch(clearSpeciesImage(context));
    }
  };
}

function clearSpeciesImage(context) {
  return {
    type: CLEAR_SPECIES_IMAGE,
    context,
  };
}

// Wildlife class

function convertDatabaseWildlifeClass(databaseWildlifeClass) {
  let wildlifeClass = undefined;

  if (databaseWildlifeClass === 'AMPHIBIA') {
    wildlifeClass = WildlifeClass.amphibian;
  }
  else if (databaseWildlifeClass === 'MAMMALIA') {
    wildlifeClass = WildlifeClass.mammal;
  }
  else if (databaseWildlifeClass === 'REPTILIA') {
    wildlifeClass = WildlifeClass.reptile;
  }
  else {
    throw new Error(`Unexpected database wildlife class value: ${databaseWildlifeClass}`);
  }

  return wildlifeClass;
}

// Wildlife threat level

function convertDatabaseThreatLevel(databaseThreatLevel) {
  let threat = undefined;

  switch (databaseThreatLevel) {
    case 3:
      threat = ThreatLevel.criticallyEndangered;
      break;
    case 4:
      threat = ThreatLevel.endangered;
      break;
    case 5:
      threat = ThreatLevel.vulnerable;
      break;
    case 6:
      threat = ThreatLevel.nearThreatened;
      break;
    case 7:
      threat = ThreatLevel.leastConcern;
      break;

    // Unsupported threat levels:
    case 0:
      // Unknown
      break;
    case 1:
      // Extinct
      break;
    case 2:
      // Extinct In Wild
      break;
    case 8:
      // Data Deficient
      break;
    case 9:
      // Not Evaluated
      break;

    // Unexpected threat levels:
    default:
      throw new Error(`Unexpected database threat level: ${databaseThreatLevel}`);
  }

  return threat;
}

// Wildlife presence

function convertDatabaseRegionPresence(databasePresence) {
  let presence = undefined;

  switch (databasePresence) {
    case 1:
      presence = "Extant";
      break;
    case 2:
      presence = "Probably Extant";
      break;
    case 3:
      presence = "Possibly Extant";
      break;
    case 4:
      presence = "Possibly Extinct";
      break;
    case 5:
      presence = "Extinct";
      break;
    case 6:
      presence = "Uncertain";
      break;

    // Unsupported presence values:
    case 0:
      // Unknown
      break;

    default:
      // Unexpected presence value:
      throw new Error(`Unexpected database presence value: ${databasePresence}`);
  }

  return presence;
}



// WEBPACK FOOTER //
// ./src/actions/wildlife.js